import React from 'react';
import { Card, Row, Col, Input, Select, Button, Space, DatePicker } from 'antd';
import { SearchOutlined, FilterOutlined, ReloadOutlined } from '@ant-design/icons';

const { RangePicker } = DatePicker;
const { Option } = Select;

interface FilterItem {
  type: 'input' | 'select' | 'dateRange' | 'custom';
  key: string;
  placeholder?: string;
  options?: Array<{ label: string; value: any }>;
  value?: any;
  onChange?: (value: any) => void;
  allowClear?: boolean;
  style?: React.CSSProperties;
  span?: number;
  customElement?: React.ReactNode;
}

interface FilterCardProps {
  filters: FilterItem[];
  onClear?: () => void;
  onRefresh?: () => void;
  loading?: boolean;
  extra?: React.ReactNode;
  style?: React.CSSProperties;
}

const FilterCard: React.FC<FilterCardProps> = ({
  filters,
  onClear,
  onRefresh,
  loading,
  extra,
  style
}) => {
  const renderFilterItem = (filter: FilterItem) => {
    const { type, key, placeholder, options, value, onChange, allowClear = true, style: itemStyle } = filter;

    switch (type) {
      case 'input':
        return (
          <Input
            placeholder={placeholder || 'Search...'}
            prefix={<SearchOutlined />}
            value={value}
            onChange={(e) => onChange?.(e.target.value)}
            allowClear={allowClear}
            style={itemStyle}
          />
        );

      case 'select':
        return (
          <Select
            placeholder={placeholder || 'Select...'}
            value={value}
            onChange={onChange}
            allowClear={allowClear}
            style={{ width: '100%', ...itemStyle }}
          >
            {options?.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        );

      case 'dateRange':
        return (
          <RangePicker
            style={{ width: '100%', ...itemStyle }}
            value={value}
            onChange={onChange}
            placeholder={placeholder ? [placeholder, placeholder] : ['Start Date', 'End Date']}
          />
        );

      case 'custom':
        return filter.customElement;

      default:
        return null;
    }
  };

  return (
    <Card className="filter-card content-card" style={style}>
      <Row gutter={[16, 16]} align="middle">
        {filters.map((filter, index) => (
          <Col key={filter.key || index} xs={24} sm={12} md={filter.span || 6}>
            {renderFilterItem(filter)}
          </Col>
        ))}
        
        <Col xs={24} sm={12} md={6}>
          <Space>
            {onClear && (
              <Button
                icon={<FilterOutlined />}
                onClick={onClear}
                style={{ 
                  backgroundColor: '#f50', 
                  borderColor: '#f50', 
                  color: 'white' 
                }}
                type="primary"
              >
                Clear Filters
              </Button>
            )}
            {onRefresh && (
              <Button
                icon={<ReloadOutlined />}
                onClick={onRefresh}
                loading={loading}
                style={{ 
                  backgroundColor: '#52c41a', 
                  borderColor: '#52c41a', 
                  color: 'white' 
                }}
                type="primary"
              >
                Refresh
              </Button>
            )}
            {extra}
          </Space>
        </Col>
      </Row>
    </Card>
  );
};

FilterCard.displayName = 'FilterCard';

export default FilterCard;
